import { KeySet } from '@tableau/data-structures';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import {
  getFirstParamValueAsSet,
  paramValuesMapToSearch,
  removeParamValue,
  searchToParamValuesMap,
  setParamValues,
  UrlSearch,
  UrlSearchProvider
} from '../../../data-catalog-shared';

export function constructNewSearchForColumnSelection(search: string, tableId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const columns = paramValuesMap[CatalogUrlParamKeys.table];
  if (columns && columns.indexOf(tableId) >= 0) {
    removeParamValue(paramValuesMap, CatalogUrlParamKeys.table, tableId);
  } else {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.table, [tableId]);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchWithClearedColumnSelection(search: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const columns = paramValuesMap[CatalogUrlParamKeys.table];
  if (columns) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.table, []);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export type VirtualConnectionTableSubplaceParams = {
  readonly urlSearchValue: string;
  readonly selectedColumns: KeySet;
  readonly updateColumnSelection: (tableId: string) => void;
  readonly clearColumnSelection: () => void;
};

export type VirtualConnectionTableSubplaceParamsProviderProps = {
  readonly children: (params: VirtualConnectionTableSubplaceParams) => React.ReactNode;
};

export class VirtualConnectionTableSubplaceParamsProvider extends React.Component<VirtualConnectionTableSubplaceParamsProviderProps> {

  public override render() {
    return (
      <UrlSearchProvider>
        {urlSearch =>
          <VirtualConnectionTableSubplaceParamsProviderInternal urlSearch={urlSearch}>
            {this.props.children}
          </VirtualConnectionTableSubplaceParamsProviderInternal>
        }
      </UrlSearchProvider>
    );
  }
}

type VirtualConnectionTableSubplaceParamsProviderInternalProps = {
  readonly urlSearch: UrlSearch;
  readonly children: (params: VirtualConnectionTableSubplaceParams) => React.ReactNode;
};

class VirtualConnectionTableSubplaceParamsProviderInternal extends React.Component<VirtualConnectionTableSubplaceParamsProviderInternalProps> {

  private readonly memoizedGetFirstParamValueAsSet = defaultMemoize(getFirstParamValueAsSet);

  public override render() {
    return this.props.children({
      urlSearchValue: this.props.urlSearch.value,
      selectedColumns: this.memoizedGetFirstParamValueAsSet(this.props.urlSearch.value, CatalogUrlParamKeys.table),
      updateColumnSelection: this.updateColumnSelection,
      clearColumnSelection: this.clearColumnSelection,
    });
  }

  private readonly clearColumnSelection = () => {
    const urlSearch = this.props.urlSearch;
    const newSearchWithClearedColumnSelection = constructNewSearchWithClearedColumnSelection(urlSearch.value);
    urlSearch.update(newSearchWithClearedColumnSelection);
  }

  private readonly updateColumnSelection = (tableId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForColumnSelection(urlSearch.value, tableId);
    urlSearch.update(newSearch);
  }

}
